import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {VivoMapping} from './vivo-mapping.model';
import {environment} from '../../environments/environment';
import {AbstractCrudService} from '../shared/abstract-crud.service';
import {subDays} from 'date-fns/esm';
import {ServiceAnswer} from '../shared/service-answer';
import { AuthService } from '~app/auth/auth.service';
import { Subject } from 'rxjs';
import { AssetSearchResponse } from './vivo.model';

/**
 * Service for interacting with Mipro VIVO API.
 */
@Injectable()
export class VivoService extends AbstractCrudService<VivoMapping> {
  /**
   * URL for current model, override url from superclass
   * @type {string}
   */
  protected url = environment.regoApiURL + 'regovivomappings';

  private mappings: VivoMapping[];

  private diaryChangedSubject = new Subject<void>();
  public diaryChanged$ = this.diaryChangedSubject.asObservable();

  /**
   * Default constructor
   * @param {HttpClient} http - refined http service, automatically adds authentication token
   * @param {AuthService} auth - authentication service
   */
  constructor(
    protected http: HttpClient,
    private auth: AuthService
  ) {
    super(http);
    this.initMappings();
  }

  private async initMappings(): Promise<void> {
    const result = await this.list();
    if (result.isOk) {
      this.mappings = result.data;
    }
  }


  /**
   * Create new data
   * @param {VivoMapping} data
   * @returns {Promise<VivoMapping>}
   */
  public async create(data: VivoMapping): Promise<ServiceAnswer<VivoMapping>> {
    const result = await super.create(data);
    this.initMappings();
    return result;
  }

  /**
   * Update existing data
   * @param {VivoMapping} data
   * @returns {Promise<VivoMapping>}
   */
  public async edit(data: VivoMapping): Promise<ServiceAnswer<VivoMapping>> {
    const result = await super.edit(data);
    this.initMappings();
    return result;
  }

  public getDiaryTaskType(): string {
    const tri_tenant = this.auth.getTriTenant();
    const tenantId = this.auth.getTenant();

    // "Mipro VIVO (testi)"
    if (tri_tenant === '5c41de7e5107c7430c68ac23') {
      return '5ed772e9f26dc011c34727d9';
    }

    switch (tenantId) {
      case 8:
        return '5bd169ac026a0c283c0fbdcf';
      case 13:
        return '5ebccf611798222768402e41';
      default:
        console.error('No diary task type available for tenant: ', tenantId);
        return '';
    }
  }

  public getVivoMappingForRegoId(regoId: string): VivoMapping {
    return this.mappings?.find(o => o?.regoId + '' === regoId + '');
  }

  public async getRegoIdForVivoMapping(vivoId: string): Promise<string> {
    const result = await this.list();
    if (result.isOk) {
      return result.data.find(s => s.vivoId + '' === vivoId + '')?.regoId;
    }
    return null;
  }

  /**
   * Get events from VIVO with Asset ID.
   * @param {string} assetID
   * @param {"Asset" | "Organization"} type
   * @returns {Promise<any>}
   */
  getEventsForFeature(assetID: string, type: 'Asset' | 'Organization'): Promise<any> {
    console.log('vivo', assetID);
    if (type === 'Organization') {
      return this.http.get<any>('https://miprovivo.trackinno.com/api/events?orgNode=' + assetID + '&type=newComment&type=assetCreated&limit=3000').toPromise();
    } else {
      return this.http.get<any>('https://miprovivo.trackinno.com/api/events?asset=' + assetID +
        '&limit=3000').toPromise(); // It's possible to fetch subassets also with includeChildren=true
    }
  }

  /**
   * Get events from VIVO with Asset ID.
   * @param assetID
   * @returns {Observable<any>}
   */
  getAssetInfo(assetID: string): Promise<any> {
    return this.http.get<any>('https://miprovivo.trackinno.com/api/assets/' + assetID).toPromise();
  }

  getAssetImage(image: string): Promise<any> {
    const index = image.indexOf('/') + 1;
    return this.http.post<any>('https://miprovivo.trackinno.com/api/upload/downloadLink', {
      bucket: 'trackinno-thumbnails',
      key: image.substr(0, index) + '100_' + image.substr(index)
    }).toPromise();
  }

  getAllAssets(limit: number, page: number): Promise<any> {
    return this.http.get<any>(`https://miprovivo.trackinno.com/api/assets?limit=${limit}&page=${page}`).toPromise();
  }

  getRecentEvents(days: number): Promise<any> {
    const start = subDays(new Date(), days);
    return this.http.get<any>('https://miprovivo.trackinno.com/api/events?endDate=' + new Date().toJSON() +
      '&limit=3000&page=1&startDate=' + start.toJSON()).toPromise();
  }

  /**
   * Get events from VIVO with Asset ID.
   * @param {string} id
   * @param {Date} start
   * @param {Date} end
   * @param {"Asset" | "Organization"} type
   * @returns {Promise<any>}
   */
  getEventsByDate(id: string, start: Date, end: Date, type: 'Asset' | 'Organization'): Promise<any> {
    if (type === 'Organization') {
      return this.http.get<any>('https://miprovivo.trackinno.com/api/events?orgNode=' + id +
        '&type=newComment&type=assetCreated&limit=3000&startDate=' + start.toISOString() +
        '&endDate=' + end.toISOString()).toPromise();
    } else {
      return this.http.get<any>('https://miprovivo.trackinno.com/api/events?asset=' + id +
        '&limit=3000&startDate=' + start.toISOString() + '&endDate=' + end.toISOString()).toPromise();
    }
  }

  /*CREATE CALLS*/
  async createComment(body: any): Promise<any> {
    const result = await this.http.post<any>('https://miprovivo.trackinno.com/api/events', body).toPromise();
    this.diaryChangedSubject.next();
    return result;
  }

  async createVivoNotification(body: any): Promise<any> {
    const result = await this.http.post<any>('https://miprovivo.trackinno.com/api/tasks', body).toPromise();
    this.diaryChangedSubject.next();
    return result;
  }

  /*GET CALLS*/
  getTask(id: string): Promise<any> {
    return this.http.get<any>('https://miprovivo.trackinno.com/api/tasks/' + id).toPromise();
  }

  getCommentsForTask(id: string): Promise<any> {
    return this.http.get<any>('https://miprovivo.trackinno.com/api/tasks/' + id + '/events?limit=25&page=1&type=newComment').toPromise();
  }

  vivoTaskPrioritys(): Promise<Array<{
    _id: string,
    name: string,
    class: string,
    default: string,
  }>> {
    return this.http.get<any>('https://miprovivo.trackinno.com/api/taskPriorities').toPromise();
  }

  getVivoUsers(): Promise<Array<{
    _id: string,
    firstName: string,
    lastName: string,
  }>> {
    return this.http.get<any>('https://miprovivo.trackinno.com/api/users').toPromise();
  }

  vivoTaskType(): Promise<any> {
    return this.http.get<any>('https://miprovivo.trackinno.com/api/formTemplates?page=1&limit=50').toPromise();
  }

  getEventsWithChildren(id: string, type: 'Asset' | 'Organization', limit: number): Promise<any> {
    return this.http.get<any>('https://miprovivo.trackinno.com/api/assets/' + id +
      '/events?orgNode=' + type + '&includeChildren=true&limit=' + limit).toPromise();
  }

  getEventsByDateWithChildren(id: string, start: Date, end: Date): Promise<any> {
    return this.http.get<any>('https://miprovivo.trackinno.com/api/assets/' + id + '/events?' +
      '&includeChildren=true&limit=3000&startDate=' + start.toISOString() + '&endDate=' + end.toISOString()).toPromise();
  }

  /*POST CALLS*/
  getVivoTasks(body: any): Promise<any> {
    return this.http.post<any>('https://miprovivo.trackinno.com/api/tasks/list', body).toPromise();
  }

  getDiaryEvents(body: any): Promise<any> {
    return this.http.post<any>('https://miprovivo.trackinno.com/api/tasks/list', body).toPromise();
  }

  vivoSearch(q: string): Promise<AssetSearchResponse> {
    const body = {
      page: 1,
      limit: 25,
      query: q
    };
    return this.http.post<AssetSearchResponse>('https://miprovivo.trackinno.com/api/assets/search', body).toPromise();
  }

  /*PUT HOMMAT*/
  async saveEdit(body: any): Promise<any> {
    const result = await this.http.put<any>('https://miprovivo.trackinno.com/api/tasks/' + body.id, {task: body}).toPromise();
    this.diaryChangedSubject.next();
    return result;
  }


}
